"use client";

import { useLoginMutation } from "@/components/parts/auth/api";
import {
  LoginPayload,
  loginValidation,
} from "@/components/parts/auth/validation";
import { ensureWebPushSubscription } from "@/components/parts/webpush/api";
import ForgotPassword from "@/components/sections/auth/forgotPassword";
import {
  CustomFormInput,
  inputFilters,
} from "@/components/shared/forms/customFormInput";
import GoogleSignInButton from "@/components/shared/googleSignInButton";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { zodResolver } from "@hookform/resolvers/zod";
import { default as Cookie, default as Cookies } from "js-cookie";
import Image from "next/image";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

const LoginPage = () => {
  const router = useRouter();
  const form = useForm<LoginPayload>({
    resolver: zodResolver(loginValidation),
    defaultValues: {
      email: "",
      password: "",
    },
  });
  const {
    handleSubmit,
    formState: { errors },
    setError,
  } = form;

  const loginMutation = useLoginMutation(setError);

  const onSubmit = (data: LoginPayload) => {
    loginMutation.mutate(data, {
      onSuccess: async (response) => {
        Cookie.remove("accessToken");
        Cookie.set("accessToken", response.data.token);
        await ensureWebPushSubscription();
        router.push("/dashboard");
      },
    });
  };

  const searchParams = useSearchParams();
  useEffect(() => {
    // Ambil token dari URL setelah redirect
    const token = searchParams.get("token") || "";
    // console.log("token=", token)

    if (token) {
      Cookies.set("accessToken", token);
      window.location.href = "/dashboard"; // Redirect ke home atau halaman lain
    }
  }, [router, searchParams]);

  return (
    <Card className="max-w-[600px] bg-white flex flex-col items-center justify-center p-8 rounded-3xl my-10 mx-4 md:mx-0">
      <Image
        src={"/assets/images/logo-pali.png"}
        alt="Logo pali"
        width={80}
        height={80}
      />
      <h1 className="my-2 font-semibold text-lg">Silahkan Masuk ALSINTAN</h1>
      <h2 className="text-sm text-center text-gray-600">
        Masukan username dan password untuk melakukan pengajuan alsintan.
      </h2>
      <Form {...form}>
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="w-full grid grid-cols-1"
        >
          <div className="grid grid-cols-1 w-full mt-5 gap-5">
            <CustomFormInput
              name="email"
              label="Email"
              placeholder="Masukkan Email"
              filterInput={inputFilters.email}
            />
            <CustomFormInput
              name="password"
              label="Password"
              placeholder="*******"
              type="password"
            />
          </div>
          <ForgotPassword />
          <Button type="submit" className="mt-5 py-5 rounded-full">
            Masuk
          </Button>
        </form>
      </Form>
      <div className="flex gap-x-5 items-center justify-center overflow-hidden my-2 md:my-5 py-3 md:py-0 w-full">
        <Separator className="w-[50%] border-t-2" />
        <p className="w-fit">atau</p>
        <Separator className="w-[50%] border-t-2" />
      </div>
      <GoogleSignInButton className="w-full rounded-full" />
      <div className="w-full text-sm mt-6 text-center">
        Belum punya akun?{" "}
        <Link
          href={`/register`}
          className="text-primary font-semibold hover:underline"
        >
          Daftar Sekarang
        </Link>
      </div>
    </Card>
  );
};

export default LoginPage;
